import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { AREAS, TODAS_LAS_ENTRADAS } from '@/app/navegacion'
import { useSesion } from '@/modules/auth/useSesion'
import { useOrdenesEnVivo } from '@/shared/datos/useOrdenesEnVivo'
import { usePendientesDeDecision } from '@/shared/datos/usePendientes'

import { BarraInferior } from './BarraInferior'
import { BarraSuperior } from './BarraSuperior'
import { MenuLateral } from './MenuLateral'
import estilos from './ArmazonApp.module.css'

/**
 * Resuelve el título y el área de la pantalla actual.
 *
 * Sale de la misma definición que dibuja el menú, para que el título de la
 * barra y el enlace resaltado no puedan contradecirse.
 */
function ubicarRuta(ruta: string) {
  const entrada = [...TODAS_LAS_ENTRADAS]
    .sort((a, b) => b.ruta.length - a.ruta.length)
    .find((e) => (e.ruta === '/' ? ruta === '/' : ruta === e.ruta || ruta.startsWith(e.ruta + '/')))

  if (!entrada) return { titulo: 'MEGA', contexto: undefined }

  const area = AREAS.find((a) => a.entradas.some((e) => e.ruta === entrada.ruta))

  // Solo el área. Antes decía «Operación · Sección 5», pero los números del
  // PDF ya no corresponden a lo que se ve —la 2 se fue al tablero, servicios
  // y zonas a donde se usan— y un número descolocado confunde más que ayuda.
  const contexto = area?.nombre

  return { titulo: entrada.etiqueta, contexto }
}

export function ArmazonApp() {
  const [menuAbierto, setMenuAbierto] = useState(false)
  const { pathname } = useLocation()
  const { perfil } = useSesion()
  const pendientes = usePendientesDeDecision()
  // Matriz, ficha y tablero se enteran solos de lo que cambia en la base.
  useOrdenesEnVivo()

  const { titulo, contexto } = ubicarRuta(pathname)

  /**
   * El técnico no tiene menú.
   *
   * Un menú es para escoger entre pantallas, y él tiene UNA. La barra lateral
   * le ocupaba un tercio del teléfono para ofrecerle dos enlaces, uno de los
   * cuales ya está donde está parado. Sin menú no hay que abrir nada, no hay
   * dónde perderse, y la pantalla entera es el trabajo.
   */
  const sinMenu = perfil?.rol === 'operario'

  // Al cambiar de pantalla el contenido arranca desde arriba. Sin esto, entrar
  // al detalle desde el final de una lista larga deja al usuario mirando el
  // pie de una página que no ha visto.
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [pathname])

  return (
    <div className={`${estilos.armazon} ${sinMenu ? estilos.sinMenu : ''}`}>
      {!sinMenu && (
        <MenuLateral
          abierto={menuAbierto}
          onCerrar={() => setMenuAbierto(false)}
          pendientes={pendientes}
        />
      )}

      <div className={estilos.principal}>
        <BarraSuperior
          titulo={titulo}
          contexto={contexto}
          onAbrirMenu={sinMenu ? undefined : () => setMenuAbierto(true)}
        />

        <main className={estilos.contenido}>
          <Outlet />
        </main>
      </div>

      {/* El menú del técnico: tres pantallas, flotante abajo. */}
      {sinMenu && <BarraInferior />}
    </div>
  )
}
